import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Star,
  ShoppingCart,
  Heart,
  Share2,
  Truck,
  Shield,
  Award,
  ChevronRight,
  Minus,
  Plus,
  AlertTriangle,
  Play,
  Sparkles,
  CheckCircle,
  Clock,
  ArrowLeft,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import ProductCard from '../../components/common/ProductCard';
import AIGuidanceModal from '../../components/common/AIGuidanceModal';
import { useCart } from '../../context/CartContext';
import { products, therapistRecommendations, conditions } from '../../data/mock';

const ProductDetailPage = () => {
  const { id } = useParams();
  const { addItem, items } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [aiModalOpen, setAiModalOpen] = useState(false);

  const product = products.find((p) => p.id === id);
  const inCart = items.find((item) => item.productId === id);

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product not found</h2>
          <Link to="/shop">
            <Button>Back to Shop</Button>
          </Link>
        </div>
      </div>
    );
  }

  const relatedProducts = products
    .filter((p) => p.id !== product.id && p.conditions?.some((c) => product.conditions?.includes(c)))
    .slice(0, 4);

  const therapistRec = therapistRecommendations.find((rec) =>
    rec.productIds.includes(product.id)
  );

  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const getBadgeConfig = (badge) => {
    switch (badge) {
      case 'therapist-recommended':
        return { label: 'Therapist Recommended', className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Award };
      case 'verified':
        return { label: 'Clinically Verified', className: 'bg-blue-50 text-blue-700 border-blue-200', icon: CheckCircle };
      case 'best-seller':
        return { label: 'Best Seller', className: 'bg-amber-50 text-amber-700 border-amber-200', icon: Star };
      default:
        return { label: badge, className: 'bg-gray-50 text-gray-700 border-gray-200', icon: null };
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-red-600">Home</Link>
            <ChevronRight className="w-4 h-4 text-gray-300" />
            <Link to="/shop" className="text-gray-500 hover:text-red-600">Shop</Link>
            <ChevronRight className="w-4 h-4 text-gray-300" />
            <span className="text-gray-900 font-medium truncate max-w-[200px]">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Back Button - Mobile */}
        <Link to="/shop" className="md:hidden flex items-center gap-2 text-gray-600 mb-4">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Shop</span>
        </Link>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl overflow-hidden relative">
              <img
                src={product.images?.[selectedImage] || 'https://placehold.co/600x600/f3f4f6/9ca3af?text=Product'}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              {discount > 0 && (
                <div className="absolute top-4 left-4 bg-red-600 text-white text-sm font-semibold px-3 py-1.5 rounded-full">
                  -{discount}% OFF
                </div>
              )}
            </div>

            {/* Thumbnail Strip */}
            {product.images?.length > 1 && (
              <div className="flex gap-3">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index
                        ? 'border-red-600 ring-2 ring-red-200'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              {product.badges?.map((badge) => {
                const config = getBadgeConfig(badge);
                return (
                  <span
                    key={badge}
                    className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full border ${config.className}`}
                  >
                    {config.icon && <config.icon className="w-4 h-4" />}
                    {config.label}
                  </span>
                );
              })}
            </div>

            {/* Title & Rating */}
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
              <p className="text-gray-600 mb-3">{product.shortDescription}</p>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < Math.floor(product.rating)
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-gray-200'
                      }`}
                    />
                  ))}
                  <span className="font-semibold text-gray-900 ml-1">{product.rating}</span>
                </div>
                <span className="text-gray-400">|</span>
                <span className="text-gray-600">{product.reviewCount} reviews</span>
              </div>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold text-gray-900">${product.price.toFixed(2)}</span>
              {product.originalPrice && (
                <span className="text-xl text-gray-400 line-through">
                  ${product.originalPrice.toFixed(2)}
                </span>
              )}
              {discount > 0 && (
                <span className="text-sm font-medium text-red-600">Save {discount}%</span>
              )}
            </div>

            {/* Stock & Delivery */}
            <div className="flex items-center gap-6 text-sm">
              {product.stock > 0 ? (
                <div className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium">In Stock ({product.stock} available)</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium">Out of Stock</span>
                </div>
              )}
              {product.fastShipping && (
                <div className="flex items-center gap-2 text-cyan-600">
                  <Truck className="w-4 h-4" />
                  <span className="font-medium">Fast Shipping (2-3 days)</span>
                </div>
              )}
            </div>

            {/* Quantity & Add to Cart */}
            <div className="flex items-center gap-4">
              <div className="flex items-center border border-gray-200 rounded-lg">
                <button
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="p-3 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-l-lg transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-12 text-center font-semibold text-gray-900">{quantity}</span>
                <button
                  onClick={() => setQuantity((q) => q + 1)}
                  className="p-3 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-r-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <Button
                size="lg"
                onClick={() => addItem(product.id, quantity)}
                disabled={product.stock === 0}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full h-12 shadow-lg shadow-red-200 hover:shadow-xl transition-all"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {inCart ? `Add More (${inCart.quantity} in cart)` : 'Add to Cart'}
              </Button>

              <Button
                variant="outline"
                size="icon"
                className="w-12 h-12 rounded-full border-gray-200 hover:border-red-300 hover:bg-red-50"
              >
                <Heart className="w-5 h-5 text-gray-400" />
              </Button>
            </div>

            {/* AI Guidance Button */}
            <Button
              variant="outline"
              onClick={() => setAiModalOpen(true)}
              className="w-full h-12 rounded-full border-gray-200 hover:border-red-300 hover:bg-red-50 group"
            >
              <Sparkles className="w-5 h-5 text-red-500 mr-2 group-hover:animate-pulse" />
              <span>Ask AI for Guidance</span>
            </Button>

            {/* Therapist Recommendation Card */}
            {therapistRec && (
              <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Award className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-emerald-800">Recommended by your therapist</p>
                      <p className="text-sm text-emerald-700 mt-1">
                        <span className="font-semibold">{therapistRec.therapistName}</span>
                        <span className="text-emerald-600"> • {therapistRec.clinic}</span>
                      </p>
                      <p className="text-sm text-emerald-600 mt-1">"{therapistRec.note}"</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Trust Indicators */}
            <div className="grid grid-cols-3 gap-3">
              <div className="flex flex-col items-center text-center p-3 bg-gray-50 rounded-xl">
                <Shield className="w-5 h-5 text-gray-400 mb-1" />
                <span className="text-xs text-gray-600">Quality Verified</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-gray-50 rounded-xl">
                <Truck className="w-5 h-5 text-gray-400 mb-1" />
                <span className="text-xs text-gray-600">Free over $75</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-gray-50 rounded-xl">
                <Clock className="w-5 h-5 text-gray-400 mb-1" />
                <span className="text-xs text-gray-600">30-Day Returns</span>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className="mt-12">
          <Tabs defaultValue="why" className="w-full">
            <TabsList className="w-full justify-start border-b border-gray-200 rounded-none bg-transparent h-auto p-0 gap-8">
              <TabsTrigger
                value="why"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-red-600 data-[state=active]:text-red-600 pb-3 px-0"
              >
                Why It Helps
              </TabsTrigger>
              <TabsTrigger
                value="how"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-red-600 data-[state=active]:text-red-600 pb-3 px-0"
              >
                How to Use
              </TabsTrigger>
              <TabsTrigger
                value="safety"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-red-600 data-[state=active]:text-red-600 pb-3 px-0"
              >
                Safety & Warnings
              </TabsTrigger>
              <TabsTrigger
                value="reviews"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-red-600 data-[state=active]:text-red-600 pb-3 px-0"
              >
                Reviews ({product.reviewCount})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="why" className="pt-6">
              <div className="max-w-3xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Why This Helps Your Condition</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{product.whyItHelps}</p>
                
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm font-medium text-gray-500">Recommended for:</span>
                  {product.conditions?.map((c) => (
                    <span key={c} className="text-sm px-3 py-1 bg-red-50 text-red-700 rounded-full">
                      {conditions.find((cond) => cond.id === c)?.name || c}
                    </span>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="how" className="pt-6">
              <div className="max-w-3xl">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">How to Use</h3>
                
                {/* Video Placeholder */}
                <div className="aspect-video bg-gray-100 rounded-xl mb-6 flex items-center justify-center relative overflow-hidden group cursor-pointer">
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300" />
                  <div className="relative z-10 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Play className="w-6 h-6 text-red-600 ml-1" />
                  </div>
                  <p className="absolute bottom-4 left-4 text-sm font-medium text-gray-700 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full">
                    Watch How-To Video
                  </p>
                </div>

                <ol className="space-y-3">
                  {product.howToUse?.map((step, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                        {index + 1}
                      </span>
                      <span className="text-gray-600">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </TabsContent>

            <TabsContent value="safety" className="pt-6">
              <div className="max-w-3xl">
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl mb-6">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-800 mb-1">Safety Information</h4>
                    <p className="text-sm text-amber-700">
                      Always consult with your healthcare provider before starting any new
                      rehabilitation program or using new equipment.
                    </p>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-4">Warnings & Precautions</h3>
                <ul className="space-y-3">
                  {product.safetyWarnings?.map((warning, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <AlertTriangle className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">{warning}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </TabsContent>

            <TabsContent value="reviews" className="pt-6">
              <div className="max-w-3xl">
                <div className="flex items-center gap-8 mb-8">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-gray-900">{product.rating}</div>
                    <div className="flex items-center justify-center gap-0.5 mt-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < Math.floor(product.rating)
                              ? 'fill-amber-400 text-amber-400'
                              : 'text-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{product.reviewCount} reviews</p>
                  </div>
                </div>

                {/* Sample Reviews */}
                <div className="space-y-6">
                  {[
                    {
                      name: 'Sarah M.',
                      rating: 5,
                      date: '2 weeks ago',
                      verified: true,
                      comment: 'Excellent product! Really helped with my recovery after knee surgery. The quality is outstanding.',
                    },
                    {
                      name: 'Michael R.',
                      rating: 4,
                      date: '1 month ago',
                      verified: true,
                      comment: 'Good product overall. Shipping was fast and the instructions were clear. Would recommend.',
                    },
                  ].map((review, index) => (
                    <div key={index} className="border-b border-gray-100 pb-6">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <span className="font-semibold text-gray-600">{review.name.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{review.name}</p>
                            <div className="flex items-center gap-2">
                              <div className="flex">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`w-3 h-3 ${
                                      i < review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-200'
                                    }`}
                                  />
                                ))}
                              </div>
                              {review.verified && (
                                <span className="text-xs text-emerald-600 font-medium">Verified Purchase</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <span className="text-sm text-gray-400">{review.date}</span>
                      </div>
                      <p className="text-gray-600">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-16">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Pairs Well With Your Plan</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {relatedProducts.map((product) => (
                <ProductCard key={product.id} product={product} compact />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* AI Guidance Modal */}
      <AIGuidanceModal
        open={aiModalOpen}
        onOpenChange={setAiModalOpen}
        product={product}
      />

      {/* Footer Spacer for mobile nav */}
      <div className="h-20 md:h-0" />
    </div>
  );
};

export default ProductDetailPage;